import {useForm} from 'react-hook-form';
import {TextFieldElement} from 'react-hook-form-mui';
import {useNavigate} from 'react-router-dom';
import {yupResolver} from '@hookform/resolvers/yup';
import {Button} from '@mui/material';
import {isAxiosError} from 'axios';
import {useSnackbar} from 'notistack';
import {object, string} from 'yup';

import {useResolveVideo} from '@entities/video';
import {getErrorMessageFromApiError} from '@shared/api';
import {CardBox} from '@shared/components';
import {ROUTE__VIDEO_DETAILS} from '@shared/constants';
import {FormRow, Header} from '@widgets/common';

import {COLOR__WHITE} from '@/theme/colors';

interface FormValues {
  channel: string;
  videoUrl: string;
}

const v8nSchema = object().shape({
  channel: string().required(),
  videoUrl: string().required(),
});

export const CreateVideoPage = () => {
  const navigate = useNavigate();
  const {enqueueSnackbar} = useSnackbar();
  const {mutateAsync: resolveVideo, isPending} = useResolveVideo();

  const {control, handleSubmit} = useForm<FormValues>({
    mode: 'onSubmit',
    resolver: yupResolver(v8nSchema),
    defaultValues: {
      channel: '',
      videoUrl: '',
    },
  });

  const onSubmit = async (values: FormValues): Promise<void> => {
    try {
      const video = await resolveVideo(values);

      enqueueSnackbar('Video created', {variant: 'success'});
      navigate(ROUTE__VIDEO_DETAILS.replace(':video_id', video.id), {
        state: {
          video: {
            id: video.id,
            name: video.name,
            channel: video.channel,
            videoUrl: values.videoUrl,
          },
        },
      });
    } catch (error) {
      const errorMessage = isAxiosError(error)
        ? getErrorMessageFromApiError(error)
        : '';

      enqueueSnackbar(`Could not create video. ${errorMessage}`, {
        variant: 'error',
      });
    }
  };

  return (
    <>
      <Header title="Create video" mb={1} />

      <CardBox bgcolor={COLOR__WHITE} maxWidth={600}>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <FormRow width="100%" mt={2} mb={3}>
            <TextFieldElement
              name="channel"
              label="Channel name"
              control={control}
              sx={{flex: 1}}
            />
          </FormRow>

          <FormRow width="100%" mt={2}>
            <TextFieldElement
              name="videoUrl"
              label="YouTube url"
              control={control}
              sx={{flex: 1}}
            />
          </FormRow>

          <FormRow width={420} mt={3}>
            <Button
              type="button"
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{flex: 1, border: '0.4px solid #CBCDD8 !important'}}
            >
              Back
            </Button>
            <Button type="submit" disabled={isPending} sx={{flex: 2}}>
              Create video
            </Button>
          </FormRow>
        </form>
      </CardBox>
    </>
  );
};
